import React from "react";
import Tab, { TabGroup } from "@amzn/meridian/tab";
import Box from "@amzn/meridian/box";
import Heading from "@amzn/meridian/heading";
import { ShiftSummaryCategory } from "../model";

interface TabItem {
  id: string;
  label: string;
}

interface ProcessPathTabsProps {
  shiftSummary?: ShiftSummaryCategory[];
  onTabChange?: (categoryId: string) => void;
}

const ProcessPathTabs: React.FC<ProcessPathTabsProps> = ({
  shiftSummary,
  onTabChange
}) => {
  // Generate tabs dynamically from shift summary data
  const tabs: TabItem[] = React.useMemo(() => {
    if (!shiftSummary || shiftSummary.length === 0) {
      return [{ id: "headcount", label: "Headcount" }]; // fallback
    }
    
    return shiftSummary.map((category) => ({
      id: category.heading,
      label: category.heading.charAt(0).toUpperCase() + category.heading.slice(1)
    }));
  }, [shiftSummary]);

  // Default to first tab
  const [currentTab, setCurrentTab] = React.useState(tabs[0].id);

  // Update current tab when tabs change (e.g., when shiftSummary data loads)
  React.useEffect(() => {
    if (tabs.length > 0 && !tabs.find(tab => tab.id === currentTab)) {
      setCurrentTab(tabs[0].id);
      onTabChange?.(tabs[0].id);
    }
  }, [tabs, currentTab, onTabChange]);

  // Handle tab changes with local state management (no routing)
  const handleTabChange = React.useCallback((tabId: string) => {
    setCurrentTab(tabId);
    onTabChange?.(tabId);
  }, [onTabChange]);

  return (
    <Box spacingInset="300" className="process-path-tabs">
      <Box className="process-path-header">
        <Box className="title-section">
          <Heading type="h400" level={2}>
            Process Path Analysis
          </Heading>
        </Box>
      </Box>

      <Box className="tabs-container">
        <TabGroup
          fill="line"
          value={currentTab}
          onChange={handleTabChange}
          aria-labelledby="process-path-tabs"
        >
          {tabs.map((tab) => (
            <Tab key={tab.id} value={tab.id} aria-controls={tab.id} id={tab.id}>
              {tab.label}
            </Tab>
          ))}
        </TabGroup>
      </Box>
    </Box>
  );
};

export default ProcessPathTabs;
